import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { renderToStaticMarkup } from "react-dom/server";
import { MapPin, ChevronRight, Lock, RefreshCw, ExternalLink } from "lucide-react";
import { GoogleMap } from "@/lib/maps/primitives";
import { useFleetLocations } from "@/lib/fleetLocations";

/**
 * CoachLocationsMiniMap — Dashboard multi-marker mini-map.
 *
 * Fleet Map UX Hardening:
 *   - Consumes the shared `useFleetLocations()` hook so it participates
 *     in the same 15-min telemetry cycle as the Fleet Map workspace.
 *     No independent Motive fetch.
 *   - Height preset ("normal" | "tall" | "extra_tall") controls the
 *     visible map area WITHOUT changing the widget's grid width.
 *   - "Refresh" (spam-guarded, 15s cooldown) and "View Full Map" links.
 *   - Auto-fit: fits Google Maps bounds to *all* valid markers on every
 *     data refresh; on refresh failure, the last known positions stay
 *     in place with a small stale-banner underneath.
 *
 * Legacy signature: `payload` prop still accepted so unit tests that
 * feed a pre-baked payload continue to work. When present, the payload
 * takes precedence over the shared hook (used for the permission-
 * denied / restricted path emitted by the aggregator).
 */

const MAX_MARKERS = 40;

// Fleet Map UX Hardening — map body height per preset. These values
// apply to the map container, not the outer card. Card auto-sizes.
const HEIGHT_PX_BY_PRESET = {
  normal:     160,
  tall:       260,
  extra_tall: 360,
};

const Restricted = () => (
  <div
    data-testid="widget-current_coach_locations"
    data-widget-tone="denied"
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[220px] flex flex-col"
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
      <Lock size={11} /> Current Coach Locations
    </div>
    <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver">Restricted</div>
  </div>
);

const Empty = ({ reason }) => (
  <div
    data-testid="widget-current_coach_locations"
    data-widget-tone="placeholder"
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[220px] flex flex-col"
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
      <MapPin size={11} /> Current Coach Locations
    </div>
    <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver"
         data-testid="widget-current_coach_locations-empty">
      {reason === "maps_key_unavailable" || reason === "maps_sdk_load_failed"
        ? "Map unavailable"
        : "No current coach locations available"}
    </div>
    <div className="mt-1 text-[11px] text-steel">
      Telemetry source: Motive (read-only).
    </div>
  </div>
);

// "Updated Xmin ago" — freshness is derived from the Motive telemetry
// timestamp (authoritative), NOT the application fetch time.
function relativeAgeLabel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return "1 hr ago";
  return `${hrs} hrs ago`;
}

// Newest Motive telemetry timestamp across a row set — the widget's
// freshness label reads THIS, not the application fetch time.
function newestMotiveTs(rows) {
  let newest = null;
  for (const r of rows) {
    if (!r.last_updated_at) continue;
    const t = new Date(r.last_updated_at).getTime();
    if (Number.isNaN(t)) continue;
    if (newest === null || t > newest) newest = t;
  }
  return newest ? new Date(newest).toISOString() : null;
}

export default function CoachLocationsMiniMap({ payload, heightPreset }) {
  const [mapErr, setMapErr] = useState(null);
  const shared = useFleetLocations();

  // NOTE: Rules of Hooks — every hook (useState, useMemo, useFleetLocations)
  // must run on every render. Compute derived state UNCONDITIONALLY, then
  // pick the branch at the bottom of the function.

  const preset = HEIGHT_PX_BY_PRESET[heightPreset] ? heightPreset : "normal";
  const mapHeightPx = HEIGHT_PX_BY_PRESET[preset];

  const rawRows = shared.rows || [];
  const rows = rawRows
    .filter((r) => r.has_location && typeof r.lat === "number" && typeof r.lng === "number")
    .slice(0, MAX_MARKERS);

  const markers = useMemo(() => rows.map((r) => {
    const info = renderToStaticMarkup(
      <div style={{ fontSize: 12, lineHeight: 1.4 }}>
        <div style={{ fontWeight: 500 }}>{r.coach_label || r.fleet_number}</div>
        <div style={{ color: "#6B747C" }}>{r.location_label || `${r.lat.toFixed(3)}, ${r.lng.toFixed(3)}`}</div>
        {typeof r.speed_mph === "number" && (
          <div style={{ color: "#6B747C" }}>{r.speed_mph} mph · {r.ignition_on ? "ignition on" : "parked"}</div>
        )}
      </div>
    );
    return {
      id: r.asset_id,
      lat: r.lat, lng: r.lng,
      label: r.coach_label || r.fleet_number,
      info: `<div>${info}</div>`,
    };
  }), [rows]);

  const authoritativeTs = newestMotiveTs(rows);

  // ---- Branch selection AFTER all hooks have run ----
  if (payload?.unavailable_reason === "permission_denied") return <Restricted />;

  // Show empty state if we've completed at least one fetch and got
  // zero valid rows AND there's no cached data. Still-loading first
  // pass is handled by the `!shared.fetchedAt` branch below.
  if (!shared.fetchedAt && rows.length === 0 && !shared.refreshError) {
    // Initial load — show the plain empty shell so we don't flash
    // "no rows" before the first fetch resolves.
    return (
      <div
        data-testid="widget-current_coach_locations"
        className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-3 min-h-[220px] flex flex-col overflow-hidden"
      >
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <MapPin size={11} /> Current Coach Locations
        </div>
        <div className="mt-2 text-[13px] text-steel"
             data-testid="widget-current_coach_locations-loading">
          Loading fleet locations…
        </div>
      </div>
    );
  }

  if (rows.length === 0) return <Empty reason="no_rows" />;
  if (mapErr) return <Empty reason={mapErr} />;

  return (
    <div
      data-testid="widget-current_coach_locations"
      className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-3 min-h-[220px] flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <MapPin size={11} /> Current Coach Locations
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="widget-current_coach_locations-refresh"
            onClick={() => shared.refresh({})}
            disabled={!shared.canManualRefresh}
            title={shared.canManualRefresh ? "Refresh locations" : "Please wait…"}
            className="inline-flex items-center gap-1 text-[10.5px] text-steel hover:text-coach-blue disabled:opacity-40"
          >
            <RefreshCw size={11} className={shared.isRefreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <div className="text-[10.5px] text-steel tabular-nums"
               data-testid="widget-current_coach_locations-count">
            {rows.length} live
          </div>
        </div>
      </div>
      <div
        className="relative rounded-sm overflow-hidden border border-chrome-mist"
        data-testid="widget-current_coach_locations-map"
        style={{ height: mapHeightPx }}
      >
        <GoogleMap
          markers={markers}
          zoom={6}
          fit="auto"
          onError={setMapErr}
          containerStyle={{ position: "absolute", inset: 0 }}
        />
      </div>
      {shared.refreshError && (
        <div
          className="mt-2 px-2 py-1 rounded-sm bg-amber-hold/10 border border-amber-hold/30 text-[11px] text-carbon dark:text-road-white flex items-center justify-between gap-2"
          data-testid="widget-current_coach_locations-refresh-error"
        >
          <span>Location refresh failed — showing last known positions.</span>
          <button
            type="button"
            onClick={() => shared.refresh({ force: true })}
            className="text-coach-blue hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div
          className="text-[10.5px] text-steel"
          data-testid="widget-current_coach_locations-freshness"
          title={authoritativeTs || "Motive timestamp unavailable"}
        >
          {authoritativeTs
            ? `Motive · updated ${relativeAgeLabel(authoritativeTs)}`
            : "Motive timestamp unavailable"}
          {" · "}Auto-refresh every 15 min
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/fleet/map"
            data-testid="widget-current_coach_locations-view-full"
            className="flex items-center gap-1 text-[11px] text-coach-blue hover:underline"
          >
            <ExternalLink size={11} /> View Full Map
          </Link>
          <Link
            to="/fleet"
            data-testid="widget-current_coach_locations-view-all"
            className="flex items-center gap-1 text-[11px] text-coach-blue hover:underline"
          >
            View Fleet <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
